import * as React from 'react';
import { AlertTriangle, ArrowRight, Check, Sparkles, X, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StageProgressBar } from '@/components/ui/stage-progress';
import {
  describeBaseFormationRun,
  useBaseFormationRunMutation,
  useBaseFormationRunQuery,
  type BaseFormationRunStatus,
} from '@/lib/api/base-formation';
import { useDocumentProcessingQuery } from '@/lib/api/document-processing';
import {
  documentReadiness,
  type BaseFormationStepState,
  type BaselineDocFileState,
  type NewEngagementWizardState,
} from '@/lib/wizard/new-engagement-state';
import { cn } from '@/lib/utils/cn';
import { formatCount, progressLabel, progressPercent } from '@/lib/utils/processing';

import { hydrateBaselineFiles } from './baseline-files';

/**
 * Step 4 — AI base formation pane; the wizard's last step.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-wizard.js
 * → stepBaseFormation()` + `wizard.css → .bf-grid / .bf-card /
 * .bf-status`: an AI-surface status card over a grid of phase cards. The
 * prototype's six cards named phases the backend never reports; the four
 * here are the ones it does — the documents' reading and indexing stages
 * (from the documents list) and the run's two stages (from the run row).
 *
 * Ordering (T-0228, C2-07): the run starts only once every uploaded
 * document is indexed. The step polls the documents list itself (the user
 * has usually left step 3 by then) and counts an upload still in flight as
 * a document to wait for — the production run that read nothing started
 * while the SOW was still uploading. With nothing uploaded, or nothing
 * that could be processed, no run is started: the step says there is
 * nothing to read and the wizard can finish.
 *
 * Honest status (C2-14): the card shows the run's real end — succeeded
 * with the sections read and items proposed, degraded with what was
 * skipped and why, failed with why — and the phase cards never show a
 * green tick over an empty or failed run. Degraded and failed runs offer
 * "Run again", which starts a new run under a fresh Idempotency-Key: the
 * key minted at wizard open is for the first attempt only, since replaying
 * it would return the run that just failed.
 *
 * Proposals are not decided here (C2-41, E20): one sentence says where
 * they are decided, and "Open AI review queue" takes the user there.
 */
export interface StepProps {
  state: NewEngagementWizardState;
  setBaseFormation: (patch: Partial<BaseFormationStepState>) => void;
  /** Functional per-row updater, so the step can carry the documents list's stages onto the rows. */
  updateBaselineFiles: (updater: (files: BaselineDocFileState[]) => BaselineDocFileState[]) => void;
  /** Stable idempotency key for the first run POST, minted by the host per wizard open. */
  runIdempotencyKey: string;
  /** Closes the wizard and opens the engagement's AI review queue. */
  onOpenQueue?: () => void;
}

/** Where proposals are decided — the one sentence every end state repeats. */
export const REVIEW_SENTENCE =
  'Proposals are decided in the AI review queue after the wizard closes; nothing waits on them.';

export function StepBaseFormation({
  state,
  setBaseFormation,
  updateBaselineFiles,
  runIdempotencyKey,
  onOpenQueue,
}: StepProps) {
  const engagementId = state.engagement?.engagement_id;
  const runId = state.baseFormation.run_id || undefined;
  const files = state.baselineDocs.files;

  // ── Follow the documents until every one is indexed ──────────────
  const docsList = useDocumentProcessingQuery(engagementId, { enabled: Boolean(engagementId) });
  React.useEffect(() => {
    const docs = docsList.data?.documents;
    if (!docs) return;
    const byId = new Map(docs.map((d) => [d.id, d] as const));
    updateBaselineFiles((rows) => hydrateBaselineFiles(rows, byId) ?? rows);
  }, [docsList.data, updateBaselineFiles]);
  const readiness = documentReadiness(files);
  const nothingToRead = readiness.ready && readiness.indexed === 0;

  // ── Start the run once, when there is something to read ───────────
  const start = useBaseFormationRunMutation();
  const startedRef = React.useRef(false);
  const startRun = React.useCallback(
    (idempotencyKey: string) => {
      if (!engagementId) return;
      setBaseFormation({ status: 'queued', run_id: '', failure_reason: null, proposal_count: 0 });
      start
        .mutateAsync({ engagementId, idempotencyKey })
        .then((res) => {
          setBaseFormation({
            run_id: res.run_id,
            status: (res.status as BaseFormationStepState['status']) ?? 'queued',
          });
        })
        .catch((err: unknown) => {
          setBaseFormation({
            status: 'failed',
            failure_reason: err instanceof Error ? err.message : 'Failed to start base formation.',
          });
        });
    },
    // The mutation object changes identity on every state change; its
    // mutateAsync does not, so only the inputs the start depends on are
    // listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engagementId, setBaseFormation],
  );
  React.useEffect(() => {
    if (!engagementId) return;
    if (state.baseFormation.status !== 'idle') return;
    if (!readiness.ready || readiness.indexed === 0) return;
    if (startedRef.current) return;
    startedRef.current = true;
    startRun(runIdempotencyKey);
  }, [
    engagementId,
    state.baseFormation.status,
    readiness.ready,
    readiness.indexed,
    startRun,
    runIdempotencyKey,
  ]);

  // ── Poll the run until it ends ────────────────────────────────────
  const runQ = useBaseFormationRunQuery(engagementId, runId);
  React.useEffect(() => {
    const d = runQ.data;
    if (!d) return;
    // The backend's statuses are the local ones; degraded stays degraded so
    // the step can say what was skipped.
    const next = d.status as BaseFormationStepState['status'];
    if (next !== state.baseFormation.status) {
      setBaseFormation({
        status: next,
        proposal_count: d.proposal_count,
        failure_reason: d.failure_reason,
      });
    } else if (d.proposal_count !== state.baseFormation.proposal_count) {
      setBaseFormation({ proposal_count: d.proposal_count });
    }
  }, [runQ.data, state.baseFormation.status, state.baseFormation.proposal_count, setBaseFormation]);

  if (!engagementId) {
    return (
      <div className="rounded-card border border-cite-broken/30 bg-paper-50 p-3 text-meta text-cite-broken">
        Engagement not yet created. Go back to step 2 (Modules) and Continue to create it.
      </div>
    );
  }

  const run = runQ.data ?? null;
  const view = baseFormationView(state.baseFormation, run, readiness);
  const phases = phaseStates(files, readiness, state.baseFormation.status, run);
  const analysis = analysisProgress(state.baseFormation.status, run);
  const proposals = state.baseFormation.proposal_count;
  const showQueue = Boolean(onOpenQueue) && proposals > 0 && view.proposalsReady;

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-ai-surface-border bg-ai-surface p-3.5">
        <div className="flex items-start gap-2.5">
          {view.tone === 'failed' ? (
            <AlertTriangle
              className="mt-0.5 h-4 w-4 flex-none text-posture-critical"
              aria-hidden="true"
            />
          ) : (
            <Zap className="mt-0.5 h-4 w-4 flex-none text-ai-ink" aria-hidden="true" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-h3 font-semibold text-ai-ink">{view.title}</div>
            <div
              className={cn(
                'mt-1 text-meta leading-[18px]',
                view.tone === 'failed' ? 'text-posture-critical' : 'text-ai-ink/80',
              )}
            >
              {view.line}
            </div>
            {view.note ? (
              <div className="mt-1 text-meta leading-[18px] text-ai-ink/80">{view.note}</div>
            ) : null}
            {view.runAgain || showQueue ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {view.runAgain ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => startRun(crypto.randomUUID())}
                  >
                    Run again
                  </Button>
                ) : null}
                {showQueue ? (
                  <Button type="button" size="sm" onClick={onOpenQueue}>
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    Open AI review queue
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {view.kind === 'reading' ? (
          <StageProgressBar
            className="mt-3"
            percent={progressPercent({ done: readiness.indexed, total: readiness.total })}
            label={readingLabel(readiness)}
            ariaLabel="Documents indexed"
            tone="ai"
            size="md"
          />
        ) : analysis ? (
          <StageProgressBar
            className="mt-3"
            percent={analysis.percent}
            label={analysis.label}
            ariaLabel="AI analysis of the baseline documents"
            tone="ai"
            size="md"
          />
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {PHASES.map((p, i) => (
          <PhaseCard key={p.title} title={p.title} sub={p.sub} status={phases[i] ?? 'wait'} />
        ))}
      </div>
    </div>
  );
}

/** "Reading documents… 1 of 3 indexed" — the wait before the run starts. */
export function readingLabel(readiness: { indexed: number; total: number }): string {
  return `Reading documents… ${formatCount(readiness.indexed)} of ${formatCount(readiness.total)} indexed`;
}

interface StepView {
  kind: 'reading' | 'nothing' | 'running' | 'succeeded' | 'degraded' | 'failed';
  tone: 'working' | 'done' | 'failed';
  title: string;
  line: string;
  /** The run's own account (busy AI service), under the line. */
  note: string | null;
  runAgain: boolean;
  /** The run ended with something to review. */
  proposalsReady: boolean;
}

const EMPTY_RUN_LINE =
  'The run found no indexed document to read, so nothing was proposed. Add documents from the Documents screen; extraction starts on its own once one is indexed.';

/**
 * What the status card says, from the local run status, the run row and
 * the documents' readiness. Pure, so every state is worded once.
 */
export function baseFormationView(
  bf: BaseFormationStepState,
  run: BaseFormationRunStatus | null,
  readiness: ReturnType<typeof documentReadiness>,
): StepView {
  const none = { note: null, runAgain: false, proposalsReady: false };
  if (bf.status === 'idle') {
    if (!readiness.ready) {
      return {
        kind: 'reading',
        tone: 'working',
        title: 'Reading documents',
        line: 'The AI pass starts on its own once every uploaded document is indexed for search.',
        ...none,
      };
    }
    if (readiness.indexed === 0) {
      return {
        kind: 'nothing',
        tone: 'working',
        title: 'Nothing to read yet',
        line:
          readiness.total === 0
            ? 'No documents were uploaded, so there is nothing to extract and no run was started. Go back to add documents, or add them later from the Documents screen; extraction starts on its own once a document is indexed.'
            : 'None of the uploaded documents could be processed, so there is nothing to extract and no run was started. Go back to retry them, or add documents later from the Documents screen; extraction starts on its own once a document is indexed.',
        ...none,
      };
    }
    // Ready with something to read: the start effect fires on this render.
    return {
      kind: 'running',
      tone: 'working',
      title: 'Base formation in progress',
      line: 'Starting the AI pass over the indexed documents.',
      ...none,
    };
  }
  const account = run ? describeBaseFormationRun(run) : null;
  if (bf.status === 'queued' || bf.status === 'running') {
    return {
      kind: 'running',
      tone: 'working',
      title: 'Base formation in progress',
      line: 'Caliber is reading the baseline documents and proposing the contract baseline and assessment suggestions.',
      note: account?.message ?? null,
      runAgain: false,
      proposalsReady: false,
    };
  }
  if (bf.status === 'succeeded') {
    const total = run?.progress?.total ?? null;
    if (total === 0) {
      // An older worker ended a run over no documents as 'succeeded'; the
      // synthesizer now reports ai.no_documents instead. Never a tick here.
      return {
        kind: 'failed',
        tone: 'failed',
        title: 'Nothing was read',
        line: EMPTY_RUN_LINE,
        note: null,
        runAgain: true,
        proposalsReady: false,
      };
    }
    const read =
      total !== null
        ? `Read ${formatCount(total)} section${total === 1 ? '' : 's'}`
        : 'Read the documents';
    return {
      kind: 'succeeded',
      tone: 'done',
      title: 'Base formation complete',
      line: `${read} and proposed ${formatCount(bf.proposal_count)} item${bf.proposal_count === 1 ? '' : 's'}. ${REVIEW_SENTENCE}`,
      note: null,
      runAgain: false,
      proposalsReady: true,
    };
  }
  if (bf.status === 'degraded') {
    const kept =
      bf.proposal_count > 0
        ? ` ${formatCount(bf.proposal_count)} item${bf.proposal_count === 1 ? ' was' : 's were'} proposed from what was read. ${REVIEW_SENTENCE}`
        : ' Nothing was proposed from what was read.';
    return {
      kind: 'degraded',
      tone: 'working',
      title: 'Base formation finished with sections skipped',
      line: `${account?.message ?? 'Some sections were skipped. Run again to read the rest.'}${kept}`,
      note: null,
      runAgain: true,
      proposalsReady: bf.proposal_count > 0,
    };
  }
  // failed
  const code = (run?.failure_detail?.code ?? bf.failure_reason ?? '').replace(/\[.*$/, '');
  if (code === 'ai.no_documents') {
    return {
      kind: 'failed',
      tone: 'failed',
      title: 'Nothing to read yet',
      line: account?.message ?? EMPTY_RUN_LINE,
      // Running again would read the same nothing; the auto path starts a
      // run when the first document is indexed.
      note: null,
      runAgain: false,
      proposalsReady: false,
    };
  }
  return {
    kind: 'failed',
    tone: 'failed',
    title: 'Base formation failed',
    line:
      account?.message ??
      bf.failure_reason ??
      'The base-formation worker failed. Run again to start over.',
    note: null,
    runAgain: true,
    proposalsReady: false,
  };
}

/**
 * The AI pass's progress line (T-0159), from the run row's real counts:
 *   - sections being read: "Analysing 40% · 5 of 12 sections"
 *   - every section read:  "Read all 12 sections · drafting proposals…"
 *   - not started yet:      no number at all
 * Null once the run has finished (the header says how it ended).
 */
export function analysisProgress(
  status: BaseFormationStepState['status'],
  data: BaseFormationRunStatus | null,
): { percent: number | null; label: string } | null {
  if (status !== 'queued' && status !== 'running') return null;
  const p = data?.progress ?? null;
  if (status === 'queued' || !p) {
    return {
      percent: null,
      label: status === 'queued' ? 'Waiting for the AI pass to start…' : 'Preparing the sections…',
    };
  }
  if (p.total > 0 && p.done >= p.total) {
    return {
      percent: 100,
      label: `Read all ${formatCount(p.total)} section${p.total === 1 ? '' : 's'} · drafting proposals…`,
    };
  }
  if (p.total === 0) return { percent: null, label: 'No sections to read · drafting proposals…' };
  return { percent: progressPercent(p), label: progressLabel('Analysing', p, 'section') };
}

// ── Phase cards ─────────────────────────────────────────────────────

const PHASES: ReadonlyArray<{ title: string; sub: string }> = [
  { title: 'Read documents', sub: 'Pages read from each uploaded file' },
  { title: 'Index for search', sub: 'Passages stored so the AI can cite them' },
  {
    title: 'Extract the contract baseline',
    sub: 'Deliverables and IV&V checkpoints, section by section',
  },
  {
    title: 'Draft assessment suggestions',
    sub: 'Register framework, domains and rubric from what was read',
  },
];

export type PhaseStatus = 'done' | 'run' | 'wait' | 'partial' | 'failed';

/**
 * One status per card. The first two follow the documents' own stages;
 * the last two follow the run: stage 1 is the windowed extraction (the
 * run's progress count), stage 2 the synthesis that follows it. A
 * degraded run marks the stage that was skipped (`[windows:…]` for stage
 * 1, `stage2` for stage 2) partial, never done; a failed or empty run
 * shows no tick at all.
 */
export function phaseStates(
  files: ReadonlyArray<BaselineDocFileState>,
  readiness: ReturnType<typeof documentReadiness>,
  status: BaseFormationStepState['status'],
  run: BaseFormationRunStatus | null,
): PhaseStatus[] {
  const stages = files.flatMap((f) => {
    if (!f.document_id) return f.status === 'uploading' ? (['uploading'] as const) : [];
    return [f.stage ?? legacyStage(f.status)];
  });
  const has = (...wanted: string[]) => stages.some((s) => wanted.includes(s));
  const read: PhaseStatus =
    readiness.total === 0
      ? 'wait'
      : has('uploading', 'reading')
        ? 'run'
        : has('indexing', 'ready')
          ? 'done'
          : 'failed';
  const index: PhaseStatus =
    readiness.total === 0
      ? 'wait'
      : has('indexing')
        ? 'run'
        : readiness.ready
          ? readiness.indexed > 0
            ? 'done'
            : 'failed'
          : has('ready')
            ? 'run'
            : 'wait';

  const reason = run?.failure_reason ?? '';
  const done = run?.progress?.done ?? 0;
  const total = run?.progress?.total ?? null;
  let extract: PhaseStatus = 'wait';
  let draft: PhaseStatus = 'wait';
  if (status === 'queued' || status === 'running') {
    extract = total !== null && total > 0 && done >= total ? 'done' : 'run';
    draft = extract === 'done' ? 'run' : 'wait';
  } else if (status === 'succeeded') {
    const empty = total === 0;
    extract = empty ? 'failed' : 'done';
    draft = empty ? 'wait' : 'done';
  } else if (status === 'degraded') {
    extract = reason.includes('windows:') ? 'partial' : 'done';
    draft = reason.includes('stage2') ? 'failed' : 'done';
  } else if (status === 'failed') {
    extract = done > 0 ? 'partial' : 'failed';
    draft = 'wait';
  }
  return [read, index, extract, draft];
}

function legacyStage(
  status: BaselineDocFileState['status'],
): NonNullable<BaselineDocFileState['stage']> {
  switch (status) {
    case 'indexed':
      return 'ready';
    case 'failed':
      return 'failed';
    case 'extracted':
      return 'indexing';
    case 'uploading':
      return 'uploading';
    default:
      return 'reading';
  }
}

const PHASE_WORDS: Record<PhaseStatus, string> = {
  done: 'done',
  run: 'in progress',
  wait: 'not started',
  partial: 'partly done',
  failed: 'failed',
};

function PhaseCard({ title, sub, status }: { title: string; sub: string; status: PhaseStatus }) {
  return (
    <div
      className="rounded-card border border-ink-100 bg-paper-0 px-4 py-3.5"
      data-phase={title}
      data-status={status}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            'inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full text-[11px] font-semibold',
            status === 'done' && 'bg-posture-healthy-tint text-posture-healthy',
            status === 'run' && 'bg-ai-surface text-ai-ink',
            status === 'wait' && 'bg-ink-100 text-posture-unassessed',
            status === 'partial' && 'bg-posture-at-risk-tint text-posture-at-risk',
            status === 'failed' && 'bg-posture-critical-tint text-posture-critical',
          )}
          role="img"
          aria-label={`${title}: ${PHASE_WORDS[status]}`}
        >
          {status === 'done' ? (
            <Check className="h-3 w-3" aria-hidden="true" />
          ) : status === 'run' ? (
            <span className="block h-2.5 w-2.5 animate-spin rounded-full border-2 border-ai-surface-border border-t-ai-ink" />
          ) : status === 'partial' ? (
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          ) : status === 'failed' ? (
            <X className="h-3 w-3" aria-hidden="true" />
          ) : null}
        </span>
        <div className="min-w-0">
          <div className="text-body-sm font-medium text-ink-900">{title}</div>
          <div className="mt-0.5 text-meta text-ink-500">{sub}</div>
        </div>
      </div>
    </div>
  );
}
